/**
 * stageDisplayNames.js
 *
 * Single source of truth for human-readable assessment stage names used
 * across the clinician assignment and reassignment UIs.
 *
 * The Autism (adultAutism) pathway uses The Owl Centre's terminology which
 * differs from the ADHD pathway:
 *
 *   Stage key  |  ADHD                         |  Autism
 *   -----------|-------------------------------|-------------------
 *   CI         |  Clinical Interview           |  History
 *   SSI        |  Semi-Structured Interview    |  Semi-Structured Interview
 *   DD         |  Diagnostic Decision          |  MDT Review
 *   PAD        |  Post-Assessment Discussion   |  Feedback Session
 *   OBS        |  (N/A)                        |  Behavioural Observation
 *   Lead       |  Lead Clinician               |  Lead Clinician
 *
 * These names are used in:
 *   - AssignCliniciansDialog.js (initial assignment flow)
 *   - ReassignCliniciansDialog.js (reassignment flow)
 *
 * Module card titles on the patient overview are driven separately by
 * patientOverviewConfig.js and do not need to match 1:1 with these names
 * (e.g. the overview card shows "Diagnostic Developmental History" for the
 * combined CI + SSI module in Autism).
 */

const ADHD_STAGE_NAMES = Object.freeze({
  CI: 'Clinical Interview',
  SSI: 'Semi-Structured Interview',
  DD: 'Diagnostic Decision',
  PAD: 'Post-Assessment Discussion',
  Lead: 'Lead Clinician',
});

const AUTISM_STAGE_NAMES = Object.freeze({
  CI: 'History',
  SSI: 'Semi-Structured Interview',
  DD: 'MDT Review',
  PAD: 'Feedback Session',
  OBS: 'Behavioural Observation',
  Lead: 'Lead Clinician',
});

// CYP ADHD uses the same four-block names as adult ADHD (decision, 21 Aug:
// four distinct blocks, adult-equivalent naming). Kept as its own map rather
// than falling through to ADHD so a future CYP-specific rename is a one-line
// change and the dialogs never silently show adult wording by accident.
const CYP_ADHD_STAGE_NAMES = Object.freeze({
  CI: 'Clinical Interview',
  SSI: 'Semi-Structured Interview',
  DD: 'Diagnostic Decision',
  PAD: 'Post-Assessment Discussion',
  Lead: 'Lead Clinician',
});

/**
 * Return the full friendly-name map for the given assessment module.
 *
 * @param {string} assessmentModule - e.g. 'adultAutism', 'adultAdhd', 'cypAdhd'
 * @returns {Object} Map of stage keys to display names
 */
export function getFriendlyStageNames(assessmentModule) {
  if (assessmentModule === 'adultAutism') return { ...AUTISM_STAGE_NAMES };
  if (assessmentModule === 'cypAdhd') return { ...CYP_ADHD_STAGE_NAMES };
  return { ...ADHD_STAGE_NAMES };
}

/**
 * Return the display label for a DD supporting clinician in the overview.
 *
 * For Autism the stage is already called "MDT Review" so no qualifier is
 * needed; for ADHD the DD supporting clinician participates specifically
 * as an MDT reviewer alongside the Diagnostic Decision lead.
 *
 * @param {string} assessmentModule - e.g. 'adultAutism' or 'adultAdhd'
 * @returns {string} Display label for DD supporting stage entry
 */
export function getDDSupportingStageLabel(assessmentModule) {
  if (assessmentModule === 'adultAutism') {
    return AUTISM_STAGE_NAMES.DD;
  }
  if (assessmentModule === 'cypAdhd') {
    return `${CYP_ADHD_STAGE_NAMES.DD} (as part of MDT Review)`;
  }
  return `${ADHD_STAGE_NAMES.DD} (as part of MDT Review)`;
}
